(function attachJudesBundleCore(root, factory) {
  const api = factory();

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  root.JudesBundleCore = api;
}(typeof globalThis !== 'undefined' ? globalThis : this, function createJudesBundleCore() {
  'use strict';

  // A parent set variant is optional: smaller routines such as Starter have none.
  const REQUIRED_RECIPE_FIELDS = ['id', 'bundleType'];

  function invariant(condition, message) {
    if (!condition) throw new Error(message);
  }

  function asPositiveInteger(value, label) {
    const number = Number(value);
    invariant(Number.isInteger(number) && number > 0, `${label} must be a positive integer`);
    return number;
  }

  function asMoneyMinor(value, label) {
    const number = Number(value);
    invariant(Number.isInteger(number) && number >= 0, `${label} must be a non-negative integer`);
    return number;
  }

  function uniqueStrings(values, label) {
    invariant(Array.isArray(values), `${label} must be an array`);
    const normalized = values.map((value) => String(value));
    invariant(new Set(normalized).size === normalized.length, `${label} must not contain duplicates`);
    return normalized;
  }

  function normalizeCatalog(rawCatalog) {
    invariant(rawCatalog && typeof rawCatalog === 'object' && !Array.isArray(rawCatalog), 'catalog must be an object');

    return Object.fromEntries(Object.entries(rawCatalog).map(([variantId, rawVariant]) => {
      invariant(rawVariant && typeof rawVariant === 'object', `catalog variant ${variantId} must be an object`);

      return [String(variantId), {
        id: String(variantId),
        sku: String(rawVariant.sku || ''),
        title: String(rawVariant.title || ''),
        productTitle: String(rawVariant.productTitle || ''),
        priceMinor: asMoneyMinor(rawVariant.priceMinor, `catalog variant ${variantId} priceMinor`),
        available: rawVariant.available !== false,
        image: rawVariant.image || null
      }];
    }));
  }

  function normalizeFixedItem(rawItem, label, catalog) {
    invariant(rawItem && typeof rawItem === 'object', `${label} must be an object`);
    const variantId = String(rawItem.variantId);
    invariant(catalog[variantId], `${label} references missing variant ${variantId}`);
    const quantity = asPositiveInteger(rawItem.quantity, `${label} quantity`);
    const giftQuantity = rawItem.giftQuantity === undefined
      ? 0
      : Number(rawItem.giftQuantity);

    invariant(Number.isInteger(giftQuantity) && giftQuantity >= 0, `${label} giftQuantity must be a non-negative integer`);
    invariant(giftQuantity <= quantity, `${label} giftQuantity cannot exceed quantity`);

    return {
      variantId,
      quantity,
      giftQuantity
    };
  }

  function normalizeChoiceGroup(rawGroup, index, catalog) {
    const label = `choiceGroups[${index}]`;
    invariant(rawGroup && typeof rawGroup === 'object', `${label} must be an object`);
    invariant(rawGroup.id, `${label} id is required`);

    const id = String(rawGroup.id);
    const quantity = asPositiveInteger(rawGroup.quantity, `${label} quantity`);
    const giftQuantity = rawGroup.giftQuantity === undefined
      ? 0
      : Number(rawGroup.giftQuantity);
    const allowedVariantIds = uniqueStrings(rawGroup.allowedVariantIds, `${label} allowedVariantIds`);
    const defaultVariantIds = Array.isArray(rawGroup.defaultVariantIds)
      ? rawGroup.defaultVariantIds.map(String)
      : [];

    invariant(allowedVariantIds.length > 0, `${label} must allow at least one variant`);
    invariant(defaultVariantIds.length === quantity, `${label} must provide exactly ${quantity} defaults`);
    invariant(Number.isInteger(giftQuantity) && giftQuantity >= 0, `${label} giftQuantity must be a non-negative integer`);
    invariant(giftQuantity <= quantity, `${label} giftQuantity cannot exceed quantity`);

    allowedVariantIds.forEach((variantId) => {
      invariant(catalog[variantId], `${label} allows missing variant ${variantId}`);
    });

    defaultVariantIds.forEach((variantId) => {
      invariant(allowedVariantIds.includes(variantId), `${label} default ${variantId} is not allowed`);
    });

    return {
      id,
      label: String(rawGroup.label || id),
      quantity,
      giftQuantity,
      allowedVariantIds,
      defaultVariantIds
    };
  }

  function normalizePreset(rawPreset, index, choiceGroups) {
    const label = `presets[${index}]`;
    invariant(rawPreset && typeof rawPreset === 'object', `${label} must be an object`);
    invariant(rawPreset.id, `${label} id is required`);
    invariant(rawPreset.selections && typeof rawPreset.selections === 'object', `${label} selections are required`);

    const selections = {};
    choiceGroups.forEach((group) => {
      const chosen = Array.isArray(rawPreset.selections[group.id])
        ? rawPreset.selections[group.id].map(String)
        : [];

      invariant(chosen.length === group.quantity, `${label} must select exactly ${group.quantity} items for ${group.id}`);
      chosen.forEach((variantId) => {
        invariant(group.allowedVariantIds.includes(variantId), `${label} selection ${variantId} is not allowed for ${group.id}`);
      });
      selections[group.id] = chosen;
    });

    return {
      id: String(rawPreset.id),
      label: String(rawPreset.label || rawPreset.id),
      selections
    };
  }

  function normalizeRecipe(rawRecipe, rawCatalog) {
    invariant(rawRecipe && typeof rawRecipe === 'object', 'recipe must be an object');
    REQUIRED_RECIPE_FIELDS.forEach((field) => invariant(rawRecipe[field], `recipe ${field} is required`));

    const catalog = normalizeCatalog(rawCatalog);
    const fixedItems = (rawRecipe.fixedItems || []).map((item, index) => (
      normalizeFixedItem(item, `fixedItems[${index}]`, catalog)
    ));
    const choiceGroups = (rawRecipe.choiceGroups || []).map((group, index) => (
      normalizeChoiceGroup(group, index, catalog)
    ));
    const presets = (rawRecipe.presets || []).map((preset, index) => (
      normalizePreset(preset, index, choiceGroups)
    ));

    invariant(new Set(choiceGroups.map((group) => group.id)).size === choiceGroups.length, 'choice group IDs must be unique');
    invariant(new Set(presets.map((preset) => preset.id)).size === presets.length, 'preset IDs must be unique');

    const pricingMode = String(rawRecipe.pricingMode || 'gift_discount');
    invariant(['gift_discount', 'standard_discount'].includes(pricingMode), 'recipe pricingMode is invalid');
    const discountCodes = rawRecipe.discountCodes || {};
    const standardDiscountAmountMinor = rawRecipe.standardDiscountAmountMinor === undefined
      ? 0
      : asMoneyMinor(rawRecipe.standardDiscountAmountMinor, 'recipe standardDiscountAmountMinor');
    if (pricingMode === 'standard_discount') {
      invariant(standardDiscountAmountMinor > 0, 'standard discount amount is required');
      invariant(discountCodes.standardDiscount, 'standard discount code is required');
    }

    return {
      id: String(rawRecipe.id),
      bundleType: String(rawRecipe.bundleType),
      parentVariantId: rawRecipe.parentVariantId ? String(rawRecipe.parentVariantId) : null,
      pricingMode,
      standardDiscountAmountMinor,
      discountCodes: {
        standardDiscount: discountCodes.standardDiscount ? String(discountCodes.standardDiscount) : '',
        giftDiscount: discountCodes.giftDiscount
          ? String(discountCodes.giftDiscount)
          : rawRecipe.discountCode ? String(rawRecipe.discountCode) : ''
      },
      fixedItems,
      choiceGroups,
      presets,
      catalog
    };
  }

  function selectedComponents(recipe, selections) {
    const paid = [];
    const gifts = [];

    recipe.choiceGroups.forEach((group) => {
      const chosen = selections && selections[group.id]
        ? selections[group.id].map(String)
        : group.defaultVariantIds;

      invariant(chosen.length === group.quantity, `${group.id} requires exactly ${group.quantity} selections`);

      chosen.forEach((variantId, index) => {
        invariant(group.allowedVariantIds.includes(variantId), `${group.id} selection ${variantId} is not allowed`);
        invariant(recipe.catalog[variantId].available, `${group.id} selection ${variantId} is unavailable`);
        const target = index >= group.quantity - group.giftQuantity ? gifts : paid;
        target.push({ variantId, quantity: 1, groupId: group.id });
      });
    });

    return { paid, gifts };
  }

  function assertAvailable(components, recipe, label) {
    components.forEach((component) => {
      invariant(recipe.catalog[component.variantId].available, `${label} variant ${component.variantId} is unavailable`);
    });
  }

  function sumComponents(components, catalog) {
    return components.reduce((sum, component) => (
      sum + (catalog[component.variantId].priceMinor * component.quantity)
    ), 0);
  }

  function aggregateCartLines(paidComponents, giftComponents, recipe, bundleId) {
    const aggregated = new Map();

    function add(component, role) {
      const current = aggregated.get(component.variantId) || {
        id: component.variantId,
        quantity: 0,
        paidQuantity: 0,
        giftQuantity: 0
      };

      current.quantity += component.quantity;
      current[`${role}Quantity`] += component.quantity;
      aggregated.set(component.variantId, current);
    }

    paidComponents.forEach((component) => add(component, 'paid'));
    giftComponents.forEach((component) => add(component, 'gift'));

    return Array.from(aggregated.values()).map((line) => {
      const role = line.giftQuantity === 0
        ? 'paid'
        : line.paidQuantity === 0
          ? 'gift'
          : 'mixed';

      return {
        id: Number(line.id),
        quantity: line.quantity,
        properties: {
          _judes_bundle_id: bundleId,
          _judes_bundle_type: recipe.bundleType,
          _judes_bundle_recipe: recipe.id,
          _judes_bundle_role: role,
          _judes_bundle_gift_quantity: String(line.giftQuantity)
        }
      };
    });
  }

  function compileBundle(rawRecipe, rawCatalog, selections, options) {
    const recipe = normalizeRecipe(rawRecipe, rawCatalog);
    const bundleId = String((options && options.bundleId) || `${recipe.id}-${Date.now()}`);
    const chosen = selectedComponents(recipe, selections || {});
    const paidComponents = [];
    const giftComponents = [];

    recipe.fixedItems.forEach((item) => {
      const paidQuantity = item.quantity - item.giftQuantity;
      if (paidQuantity > 0) paidComponents.push({ variantId: item.variantId, quantity: paidQuantity });
      if (item.giftQuantity > 0) giftComponents.push({ variantId: item.variantId, quantity: item.giftQuantity });
    });
    paidComponents.push(...chosen.paid);
    giftComponents.push(...chosen.gifts);

    if (recipe.pricingMode === 'standard_discount') {
      paidComponents.push(...giftComponents.splice(0));
    }

    assertAvailable(recipe.fixedItems, recipe, 'fixed');

    const paidSubtotalMinor = sumComponents(paidComponents, recipe.catalog);
    const giftValueMinor = sumComponents(giftComponents, recipe.catalog);
    const retailValueMinor = paidSubtotalMinor + giftValueMinor;
    const discountAmountMinor = recipe.pricingMode === 'standard_discount'
      ? recipe.standardDiscountAmountMinor
      : giftValueMinor;
    invariant(discountAmountMinor <= retailValueMinor, 'discount cannot exceed retail value');
    const finalPriceMinor = retailValueMinor - discountAmountMinor;
    const cartLines = aggregateCartLines(paidComponents, giftComponents, recipe, bundleId);
    const discountCode = recipe.pricingMode === 'standard_discount'
      ? recipe.discountCodes.standardDiscount
      : recipe.discountCodes.giftDiscount;

    return {
      recipeId: recipe.id,
      bundleId,
      parentVariantId: recipe.parentVariantId,
      pricing: {
        mode: recipe.pricingMode,
        paidSubtotalMinor,
        giftValueMinor,
        retailValueMinor,
        finalPriceMinor,
        savingsMinor: discountAmountMinor
      },
      discount: {
        code: discountCode,
        amountMinor: discountAmountMinor,
        targetVariantIds: Array.from(new Set(
          (recipe.pricingMode === 'standard_discount' ? paidComponents : giftComponents)
            .map((component) => component.variantId)
        ))
      },
      paidComponents,
      giftComponents,
      cartPayload: { items: cartLines }
    };
  }

  return {
    compileBundle,
    normalizeRecipe
  };
}));
